import i18next from 'i18next';
import { toast } from 'sonner';

const SUCCESS_DEFAULTS = Object.freeze({
    reportGenerated: 'Detailed report generated',
    comparisonGenerated: 'Comparison report generated',
    jobCreated: 'Report job created',
    jobUpdated: 'Report job updated',
    jobQueued: 'Report job queued',
    linkCreated: 'Secure download link created',
    lifecycleUpdated: 'Report lifecycle updated',
    narrativeGenerated: 'Executive narrative draft generated',
    narrativeUpdated: 'Executive narrative updated',
    narrativeRemoved: 'Executive narrative removed',
});

export class DetailedReportingIntegration {
    constructor(service) { this.service = service; }

    generate(input) { return this.#mutate(() => this.service.generate(input), 'reportGenerated'); }
    getAiConfiguration(reportKey) { return this.#read(() => this.service.getAiConfiguration(reportKey)); }
    listSnapshots(reportKey) { return this.#read(() => this.service.listSnapshots(reportKey)); }
    compareSnapshots(baselineId, targetId) { return this.#read(() => this.service.compareSnapshots(baselineId, targetId)); }
    createComparisonSession(baselineId, targetId, locale) { return this.#mutate(() => this.service.createComparisonSession(baselineId, targetId, { locale }), 'comparisonGenerated'); }
    listJobs() { return this.#read(() => this.service.listJobs()); }
    createJob(input) { return this.#mutate(() => this.service.createJob(input), 'jobCreated'); }
    updateJob(id, input) { return this.#mutate(() => this.service.updateJob(id, input), 'jobUpdated'); }
    runJob(id) { return this.#mutate(() => this.service.runJob(id), 'jobQueued'); }
    listArchives(reportKey) { return this.#read(() => this.service.listArchives(reportKey)); }
    createDownloadLink(id) { return this.#mutate(() => this.service.createDownloadLink(id), 'linkCreated'); }
    downloadArchive(id, token) { return this.#read(() => this.service.downloadArchive(id, token)); }
    updateLifecycle(id, input) { return this.#mutate(() => this.service.updateLifecycle(id, input), 'lifecycleUpdated'); }
    listNarratives(snapshotId) { return this.#read(() => this.service.listNarratives(snapshotId)); }
    generateNarrative(input) { return this.#mutate(() => this.service.generateNarrative(input), 'narrativeGenerated'); }
    regenerateNarrative(id) { return this.#mutate(() => this.service.regenerateNarrative(id), 'narrativeGenerated'); }
    updateNarrative(id, input) { return this.#mutate(() => this.service.updateNarrative(id, input), 'narrativeUpdated'); }
    deleteNarrative(id) { return this.#mutate(() => this.service.deleteNarrative(id), 'narrativeRemoved'); }

    async #read(operation) {
        try { const response = await operation(); return { success: true, data: response.data }; }
        catch (error) { return { success: false, error: this.#localizedError(error) }; }
    }

    async #mutate(operation, key) {
        try {
            const response = await operation();
            toast.success(i18next.t(`reportSurface:detailedReporting.${key}`, {
                defaultValue: SUCCESS_DEFAULTS[key] || key,
            }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this.#localizedError(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    #localizedError(error) {
        if (error?.code === 'DETAILED_REPORT_AI_UNAVAILABLE') {
            return i18next.t('reportSurface:detailedReporting.aiUnavailable', {
                defaultValue: 'Controlled management narrative is disabled for this tenant or the AI provider is not configured.',
            });
        }

        const statusCode = Number(error?.statusCode ?? error?.status);
        if (statusCode === 401 || statusCode === 403) {
            return i18next.t('reportSurface:detailedReporting.accessDenied', {
                defaultValue: 'You do not have permission for this detailed reporting operation.',
            });
        }
        if ([502, 503, 504].includes(statusCode)) {
            return i18next.t('reportSurface:detailedReporting.serviceUnavailable', {
                defaultValue: 'The reporting service is currently unavailable. Please try again later.',
            });
        }
        if (statusCode === 400 || statusCode === 422) {
            return i18next.t('reportSurface:detailedReporting.requestInvalid', {
                defaultValue: 'The report request could not be validated. Check the configuration and try again.',
            });
        }
        return i18next.t('reportSurface:detailedReporting.requestFailed', {
            defaultValue: 'The detailed reporting request could not be completed. Please try again.',
        });
    }
}
